/**
 * A run whose chat session holds no messages, only its outcome.
 *
 * Two kinds: a run the agent's pre-run script settled on its own, without
 * waking the model (KAN-1087, ``metadata.script_run``), and a run that failed
 * before the agent could start, no credit or no owner, say (KAN-1047,
 * ``metadata.run_outcome``, result ``failed``). Both get a session so they show
 * up in the run history; the outcome lives in the session's metadata and on the
 * run itself. ``null`` for every other session, and for anything malformed, so
 * a stray value never renders as such a run.
 */
export type ScriptRunResult = 'no_work' | 'done' | 'error' | 'failed';

export interface IScriptRun {
  runId: string;
  result: ScriptRunResult;
}

const RESULTS: ReadonlySet<string> = new Set<ScriptRunResult>(['no_work', 'done', 'error']);

function readMarker(raw: unknown, results: ReadonlySet<string>): IScriptRun | null {
  if (!raw || typeof raw !== 'object') return null;
  const { run_id: runId, result } = raw as { run_id?: unknown; result?: unknown };
  if (typeof runId !== 'string' || typeof result !== 'string' || !results.has(result)) {
    return null;
  }
  return { runId, result: result as ScriptRunResult };
}

export function readScriptRun(
  metadata: Record<string, unknown> | null | undefined
): IScriptRun | null {
  return (
    readMarker(metadata?.script_run, RESULTS) ??
    readMarker(metadata?.run_outcome, new Set<ScriptRunResult>(['failed']))
  );
}

/** Message key under ``agentRuns`` naming the outcome, in the list and on the run. */
export const SCRIPT_RUN_LABEL_KEYS = {
  no_work: 'scriptRunNoWork',
  done: 'scriptRunDone',
  error: 'scriptRunFailed',
  failed: 'scriptRunFailed',
} as const satisfies Record<ScriptRunResult, string>;
